import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import api from './api';

class WebRTCService {
    public onRemoteStream?: (socketId: string, stream: MediaStream, userId: string) => void;
    public onPeerLeft?: (socketId: string) => void;

    private client: IAgoraRTCClient;
    private localAudioTrack: ILocalAudioTrack | null = null;
    private localVideoTrack: ILocalVideoTrack | null = null;
    private mediaStream: MediaStream | null = null;
    private isJoined: boolean = false;
    private joiningPromise: Promise<void> | null = null;

    constructor() {
        // Create an Agora Client
        this.client = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });

        this.client.on('user-published', async (user, mediaType) => {
            await this.client.subscribe(user, mediaType);
            
            // To maintain compatibility with existing components expecting a MediaStream
            let stream = new MediaStream();
            if (user.audioTrack) {
                stream.addTrack(user.audioTrack.getMediaStreamTrack());
                user.audioTrack.play();
            }
            if (user.videoTrack) {
                stream.addTrack(user.videoTrack.getMediaStreamTrack());
                (stream as any).agoraVideoTrack = user.videoTrack;
            }
            
            if (this.onRemoteStream) {
                this.onRemoteStream(user.uid.toString(), stream, user.uid.toString());
            }
        });

        this.client.on('user-unpublished', (user) => {
            if (this.onPeerLeft) {
                this.onPeerLeft(user.uid.toString());
            }
        });
    }

    private async getToken(channelName: string, role: 'publisher' | 'subscriber' = 'publisher'): Promise<{token: string, appId: string, uid: number}> {
        const res = await api.get(`/agora/token?channelName=${channelName}&role=${role}`);
        return res.data;
    }

    public async joinRoom(roomId: string, userId: string) {
        if (this.isJoined) return;
        if (this.joiningPromise) return this.joiningPromise;
        
        this.joiningPromise = (async () => {
            try {
                await this.client.setClientRole('host');
                const { token, appId, uid } = await this.getToken(roomId, 'publisher');
                
                // uid 0 means let Agora assign. The returned uid from our server is 0, but client.join requires a number/string.
                // It's safer to pass a generated string or just let Agora handle it if we pass null/undefined, but we pass uid (0).
                const joinedUid = await this.client.join(appId, roomId, token, uid || null);
                this.isJoined = true;

                if (this.localAudioTrack) {
                    await this.client.publish([this.localAudioTrack]);
                }
            } catch (err) {
                console.error("Agora join error:", err);
                throw err;
            } finally {
                this.joiningPromise = null;
            }
        })();
        return this.joiningPromise;
    }

    public async leaveRoom() {
        this.client.remoteUsers.forEach(user => {
            if (user.audioTrack) user.audioTrack.stop();
            if (user.videoTrack) user.videoTrack.stop();
        });
        await this.client.leave();
        this.isJoined = false;
        if (this.localAudioTrack) {
            this.localAudioTrack.close();
            this.localAudioTrack = null;
        }
        if (this.localVideoTrack) {
            this.localVideoTrack.close();
            this.localVideoTrack = null;
        }
    }

    public setLocalStream(stream: MediaStream) {
        this.mediaStream = stream;
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
            this.localAudioTrack = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: audioTrack });
        }
    }

    public hasLocalAudioTrack() {
        return !!this.localAudioTrack;
    }

    public async publishLocalAudio() {
        if (this.localAudioTrack && this.isJoined) {
            await this.client.publish([this.localAudioTrack]);
        }
    }

    public toggleAudio(isMuted: boolean) {
        if (this.localAudioTrack) {
            this.localAudioTrack.setMuted(isMuted);
        }
    }

    public async unpublishLocalAudio() {
        if (this.localAudioTrack) {
            await this.client.unpublish([this.localAudioTrack]);
            this.localAudioTrack.close();
            this.localAudioTrack = null;
            this.mediaStream = null;
        }
    }

    public async startBroadcasting(streamId: string, stream: MediaStream, providedUserId?: string, serverUrl?: string) {
        this.client.setClientRole('host');
        const { token, appId, uid } = await this.getToken(streamId, 'publisher');
        await this.client.join(appId, streamId, token, uid);

        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];

        const tracksToPublish = [];
        if (videoTrack) {
            this.localVideoTrack = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: videoTrack });
            tracksToPublish.push(this.localVideoTrack);
        }
        if (audioTrack) {
            this.localAudioTrack = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: audioTrack });
            tracksToPublish.push(this.localAudioTrack);
        }

        if (tracksToPublish.length > 0) {
            await this.client.publish(tracksToPublish);
        }
    }

    public async publishAsGuest(stream: MediaStream) {
        await this.client.setClientRole('host');
        
        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];

        const tracksToPublish = [];
        if (videoTrack) {
            this.localVideoTrack = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: videoTrack });
            tracksToPublish.push(this.localVideoTrack);
        }
        if (audioTrack) {
            this.localAudioTrack = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: audioTrack });
            tracksToPublish.push(this.localAudioTrack);
        }

        if (tracksToPublish.length > 0) {
            await this.client.publish(tracksToPublish);
        }
    }

    public async replaceVideoTrack(newVideoTrack: MediaStreamTrack) {
        if (this.localVideoTrack) {
            const newTrack = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: newVideoTrack });
            await this.client.unpublish(this.localVideoTrack);
            this.localVideoTrack.close();
            this.localVideoTrack = newTrack;
            await this.client.publish(this.localVideoTrack);
        }
    }

    public async watchStream(streamId: string, userId: string) {
        this.client.setClientRole('audience');
        const { token, appId, uid } = await this.getToken(streamId, 'publisher');
        await this.client.join(appId, streamId, token, uid);
    }

    public async stopViewing() {
        await this.client.leave();
    }
}

export const webRTCService = new WebRTCService();
export default webRTCService;
